import getConfig from 'next/config'

import type { Context as CartContext } from '@grandvisionhq/carts-v2'
import { extractSkuQuantityPairsFromLineItems } from '@grandvisionhq/carts-v2'
import type {
  CheckoutSettings,
  Consent,
  Routes,
  ShippingMethodsLogos,
} from '@grandvisionhq/checkout-v2'
import { AddressLineFormat } from '@grandvisionhq/checkout-v2'
import type { IntlContextModel } from '@grandvisionhq/state'
import { Util as StoreUtils } from '@grandvisionhq/stores-v2'
interface SettingsInput {
  cart: CartContext.CartContextModel['cart']
  intl: IntlContextModel
  shippingMethodsLogos?: ShippingMethodsLogos
}

const { publicRuntimeConfig } = getConfig()
const { orderTimeOut } = publicRuntimeConfig.checkout

export const basePath = '/checkout'
export const routes: Routes = {
  login: {
    label: 'Informations',
    path: `${basePath}/login`,
  },
  shipping: {
    label: 'Livraison',
    path: `${basePath}/livraison`,
  },
  overview: {
    label: 'Récapitulatif',
    path: `${basePath}/recapitulatif`,
  },
  payment: {
    label: 'Paiement',
    path: `${basePath}/paiement`,
  },
}

const getCheckoutConsents = ({
  cart,
  intl,
}: {
  cart: CartContext.CartContextModel['cart']
  intl: IntlContextModel
}): Consent[] => {
  const { getLabel, getFormattedLabel } = intl
  const hasContactLenses = cart?.contains.contactLenses

  const allConsents: Consent[] = [
    {
      title: getFormattedLabel('checkout.consent.conditions', {
        parseLink: true,
        openLinksInNewTab: true,
        parseHTML: false,
      }),
      value: 'terms-conditions',
      required: true,
      errorMessage: getLabel('checkout.consent.conditions.error'),
    },
  ]

  if (hasContactLenses) {
    allConsents.push({
      title: getLabel('checkout.consent.prescription'),
      value: 'cl-prescription-consent',
      required: true,
      errorMessage: getLabel('checkout.consent.prescription.error'),
    })
  }

  return allConsents
}

export const getCheckoutConfig = ({
  cart,
  intl,
  shippingMethodsLogos,
}: SettingsInput): CheckoutSettings => {
  const itemsToBeShipped = cart?.lineItems
    ? extractSkuQuantityPairsFromLineItems(cart.lineItems)
    : []

  const formFieldsMap = {
    billingAddress: [
      { field: 'salutation', required: true },
      { field: 'firstName', required: true },
      { field: 'lastName', required: true },
      { field: 'streetName', required: true },
      { field: 'additionalAddressInfo', required: false },
      { field: 'additionalStreetInfo', required: false },
      { field: 'city', required: true },
      { field: 'postalCode', required: true },
    ],
    deliveryAddress: [
      { field: 'salutation', required: true },
      { field: 'firstName', required: true },
      { field: 'lastName', required: true },
      { field: 'streetName', required: true },
      { field: 'additionalAddressInfo', required: false },
      { field: 'additionalStreetInfo', required: false },
      { field: 'city', required: true },
      { field: 'postalCode', required: true },
    ],
    contactDetails: [
      { field: 'email', required: true },
      { field: 'phone', required: true },
      {
        field: 'dateOfBirth',
        required: true,
      },
    ],
  }
  return {
    formFieldsMap,
    account: {
      implicitAccountCreation: true,
      createAccountRequired: true,
    },
    consent: {
      items: getCheckoutConsents({ intl, cart }),
      implicitTermsAndConditions: false,
    },
    pickupPoints: {
      showCityField: true,
    },
    shipping: {
      explicitShippingMethod: true,
    },
    shippingDetails: {
      loadingFinished: Boolean(cart) || cart === null,
      ...(cart?.billingAddress ? { billingAddress: cart.billingAddress } : {}),
      ...(cart?.shippingAddress ? { deliveryAddress: cart.shippingAddress } : {}),
      ...(cart?.customerDetails ? { customer: cart.customerDetails } : {}),
    },
    shippingFormFields: {
      billingAddress: [
        'salutation',
        'firstName',
        'lastName',
        'streetName',
        'additionalStreetInfo',
        'postalCode',
        'city',
      ],
      contactDetails: ['email', 'phone', 'dateOfBirth'],
    },
    shippingMethodsLogos: shippingMethodsLogos ? shippingMethodsLogos : {},
    storeFinder: {
      showUseMyLocation: true,
      searchWhitelist: ['storeName', 'postalCode', 'streetName', 'town'] as StoreUtils.SearchKey[],
    },
    ...(orderTimeOut ? { orderCreationTimeOutLimit: orderTimeOut } : {}),
    features: {
      isCompanyEnabled: true,
      isServiceStoreEnabled: false,
    },
    itemsToBeShipped,
    addressLookup: {
      addressLineFormat: AddressLineFormat.streetNumber_streetName,
      shouldCombineAddress: true,
    },
    login: {
      disableSignup: true,
      returnPath: cart ? `${routes.shipping?.path}?cartId=${cart.id}` : routes.shipping?.path,
    },
    routes,
    payments: {
      applePay: {
        merchantName: 'GrandOptical',
      },
    },
    logoVariant: 'alternative',
  }
}
